import React, { useEffect } from 'react';
import { Breadcrumbs, FileUploader, Input, Switch } from '@cokitchen/cokitchen-components';
import PageHeaderText from 'components/base/page-header-text';
import { ROUTE_KEYS, STORAGE_KEYS } from 'utils/constants';
import useSectionFormContext from 'contexts/section-form.context';
import { ReactComponent as HexagonIcon } from 'assets/icons/hexagon.svg';
import Select from 'components/base/select';
import { POLYGON_COLORS } from './utils';
import { useParams } from 'react-router-dom';

const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN) as string;

const SectionForm = () => {
  const { sectionId } = useParams();
  const editRoute = `manage-section/${sectionId}/edit`;
  const { setDisabled, sectionForm, brands, handleChange, brandItems, handleToggle } =
    useSectionFormContext();

  const breadcrumbs = [
    { name: 'Manage homepage', path: ROUTE_KEYS.MANAGE_SECTIONS },
    {
      name: `${sectionId ? 'Edit' : 'New'} section`,
      path: !sectionId ? ROUTE_KEYS.MANAGE_CREATE_SECTION : editRoute
    }
  ];

  useEffect(() => {
    if (
      !sectionForm.sectionName ||
      sectionForm.selectedBrand.length < 1 ||
      sectionForm.selectedItems.length < 1 ||
      sectionForm.images.length < 1 ||
      sectionForm.selectedPolygon.length < 1
    ) {
      setDisabled(true);
    } else {
      setDisabled(false);
    }
  }, [sectionForm]);

  return (
    <div className='max-w-2xl'>
      <Breadcrumbs breadcrumbs={breadcrumbs} />
      <div className='mt-8'>
        <PageHeaderText
          title={`${!sectionId ? 'Create' : 'Edit'} Section`}
          description={`${!sectionId ? 'Create' : 'Edit'} a ${
            sectionId ? '' : 'new'
          } section lorem dolum ipsum`}
        />
      </div>
      <Input
        type='text'
        label='Section Name'
        name='sectionName'
        value={sectionForm.sectionName}
        onChange={(e) => handleChange(e.target.name, e.target.value)}
        className='mb-6'
        placeholder='Enter section name'
        inputClass='h-12'
      />
      <fieldset className='mb-6'>
        <label className='font-medium text-sm'>Select Brand(s)</label>
        <Select
          multiple
          hasSearch
          placeholder='Choose brand(s) from the list'
          values={sectionForm.selectedBrand}
          options={brands.map((brand) => ({
            text: brand.name,
            value: brand.id
          }))}
          onChange={(val) => handleChange('selectedBrand', val)}
          onSearch={(val) => handleChange('brandSearch', val)}
        />
      </fieldset>
      <fieldset className='mb-6'>
        <label className='font-medium text-sm'>Select items(s)</label>
        <Select
          multiple
          hasSearch
          placeholder='Choose item(s) from the list'
          values={sectionForm.selectedItems}
          options={brandItems.map((item) => ({
            text: item.text,
            value: item.value
          }))}
          onChange={(val) => handleChange('selectedItems', val)}
          onSearch={(val) => handleChange('brandItemSearch', val)}
        />
      </fieldset>
      <fieldset>
        <label className='font-medium mt-5'>Add section image</label>
        <FileUploader
          className='p-0 border-0'
          token={token}
          setFileUrls={(urls) => {
            handleChange('images', urls);
          }}
          fileUrls={sectionForm.images}
        />
      </fieldset>
      {sectionForm.selectedPolygon.length > 0 ? (
        <div className='mt-5 border border-gray-1 rounded-xl shadow-md overflow-hidden max-w-full '>
          <div className='flex w-full border-b border-gray-1 font-semibold text-sm bg-pink-50'>
            <div className='flex-1 px-4 py-2 text-gray-5'>Select Polygons</div>
          </div>

          <ul>
            {sectionForm.selectedPolygon.map((polygon, index) => (
              <li
                key={polygon.id}
                className={`flex items-center text-sm ${
                  index !== sectionForm.selectedPolygon.length - 1 ? 'border-b border-gray-1' : ''
                }`}>
                <div className='flex-1 flex items-center gap-3 px-4 py-2'>
                  <HexagonIcon style={{ color: POLYGON_COLORS[index] }} />
                  {polygon.name}
                </div>

                <div className='w-32 px-4 py-4 text-center'>
                  <Switch
                    active={polygon.available ?? false}
                    onChange={() => handleToggle(polygon.id ?? '')}
                  />
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <p>No polygons found for this co-kitchen.</p>
      )}

      <div className='pr-9 pl-4 mt-6'>
        <p className='font-semibold mt-5 mb-1 text-xs '>Status</p>
        <div className={`flex items-center text-sm border-b border-gray-1/70`}>
          <div className='flex-1 flex items-center gap-3  font-medium text-base text-gray-6/80'>
            <span className='bg-green-5 w-3 h-3 rounded-full' />
            Active
          </div>

          <div className=' pt-2 pb-1 text-center'>
            <Switch active={sectionForm.status} onChange={(arg) => handleChange('status', arg)} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SectionForm;
